/* ==========================================================================
   Lighthouse Studios — theme.js
   Vanilla, dependency-free. Progressive enhancement: every interaction below
   has a working no-JS fallback (real forms, real links).
   ========================================================================== */
(function () {
  'use strict';

  var T = window.LighthouseTheme || {};
  var ROUTES = T.routes || {};
  var STRINGS = T.strings || {};

  /* --- helpers ----------------------------------------------------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }
  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var format = T.moneyFormat || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

    function build(decimals, sep) {
      var whole = withCommas;
      if (decimals === 0) return whole;
      return whole + (sep || '.') + parts[1];
    }

    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1.'))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, build(2, ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, build(0))
      .replace(/\{\{\s*amount\s*\}\}/g, build(2));
  }

  /* --- accessible live region -------------------------------------------- */
  var liveRegion = null;
  var liveTimer = null;
  function announce(message) {
    liveRegion = liveRegion || qs('[data-live-region]');
    if (!liveRegion || !message) return;
    liveRegion.textContent = message;
    liveRegion.classList.add('is-visible');
    clearTimeout(liveTimer);
    liveTimer = setTimeout(function () { liveRegion.classList.remove('is-visible'); }, 3600);
  }

  /* --- scroll lock + focus trap ------------------------------------------ */
  var openLayers = [];
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, event) {
    var nodes = qsa(FOCUSABLE, container).filter(function (n) {
      return n.offsetWidth || n.offsetHeight || n.getClientRects().length;
    });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openLayer(el, opts) {
    if (!el) return;
    opts = opts || {};
    var scrim = qs('[data-scrim]');
    el.hidden = false;
    // force a frame so the transition runs
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    el.setAttribute('aria-hidden', 'false');
    if (scrim && opts.scrim !== false) scrim.hidden = false;
    document.body.classList.add('is-locked');
    openLayers.push({ el: el, returnFocus: document.activeElement, opts: opts });
    var target = opts.focus ? qs(opts.focus, el) : qs(FOCUSABLE, el);
    if (target) setTimeout(function () { target.focus(); }, 60);
  }

  function closeLayer(el) {
    var index = -1;
    for (var i = openLayers.length - 1; i >= 0; i--) {
      if (!el || openLayers[i].el === el) { index = i; break; }
    }
    if (index === -1) return;
    var layer = openLayers.splice(index, 1)[0];
    layer.el.classList.remove('is-open');
    layer.el.setAttribute('aria-hidden', 'true');
    if (layer.opts.hideOnClose !== false) {
      setTimeout(function () {
        if (!layer.el.classList.contains('is-open')) layer.el.hidden = true;
      }, layer.opts.hideDelay != null ? layer.opts.hideDelay : 320);
    }
    if (!openLayers.length) {
      var scrim = qs('[data-scrim]');
      if (scrim) scrim.hidden = true;
      document.body.classList.remove('is-locked');
    }
    if (layer.returnFocus && layer.returnFocus.focus) layer.returnFocus.focus();
  }

  function closeTopLayer() {
    if (openLayers.length) closeLayer(openLayers[openLayers.length - 1].el);
  }

  on(document, 'keydown', function (e) {
    if (e.key === 'Escape') { closeTopLayer(); return; }
    if (e.key === 'Tab' && openLayers.length) {
      trapFocus(openLayers[openLayers.length - 1].el, e);
    }
  });

  on(qs('[data-scrim]'), 'click', closeTopLayer);

  /* --- mobile nav -------------------------------------------------------- */
  qsa('[data-nav-open]').forEach(function (btn) {
    on(btn, 'click', function () {
      var nav = qs('[data-mobile-nav]');
      openLayer(nav);
      btn.setAttribute('aria-expanded', 'true');
    });
  });
  qsa('[data-nav-close]').forEach(function (btn) {
    on(btn, 'click', function () {
      closeLayer(qs('[data-mobile-nav]'));
      qsa('[data-nav-open]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    });
  });

  /* --- search panel + predictive search ---------------------------------- */
  (function search() {
    var panel = qs('[data-search-panel]');
    if (!panel) return;

    qsa('[data-search-open]').forEach(function (btn) {
      on(btn, 'click', function () {
        openLayer(panel, { focus: 'input[type="search"]', scrim: true });
      });
    });
    qsa('[data-search-close]').forEach(function (btn) {
      on(btn, 'click', function () { closeLayer(panel); });
    });

    var input = qs('input[type="search"]', panel);
    var results = qs('[data-predictive-results]', panel);
    if (!input || !results || !ROUTES.predictiveSearch) return;

    var controller = null;

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < 2) { results.innerHTML = ''; results.hidden = true; return; }
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;

      var url = ROUTES.predictiveSearch +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,page,article' +
        '&resources[limit]=6' +
        '&section_id=predictive-search';

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fragment = doc.querySelector('[data-predictive-inner]');
          results.innerHTML = fragment ? fragment.innerHTML : '';
          results.hidden = !results.innerHTML.trim();
        })
        .catch(function () { /* aborted or offline — leave the real form as fallback */ });
    }, 240);

    on(input, 'input', run);
    on(input, 'focus', run);
  })();

  /* --- cart -------------------------------------------------------------- */
  var Cart = {
    busy: false,

    drawer: function () { return qs('[data-cart-drawer]'); },

    open: function () {
      var drawer = this.drawer();
      if (!drawer) { window.location.href = ROUTES.cart; return; }
      openLayer(drawer, { hideOnClose: false });
    },

    close: function () { closeLayer(this.drawer()); },

    setBusy: function (state) {
      this.busy = state;
      var drawer = this.drawer();
      if (drawer) drawer.classList.toggle('is-busy', !!state);
    },

    /* Re-render drawer + count from a section render so markup stays in Liquid */
    refresh: function (openAfter) {
      var self = this;
      var sections = ['cart-drawer', 'cart-icon-bubble'];
      return fetch(ROUTES.root + '?sections=' + sections.join(','), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data['cart-drawer']) {
            var doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
            var fresh = doc.querySelector('[data-cart-drawer-inner]');
            var target = qs('[data-cart-drawer-inner]');
            if (fresh && target) target.innerHTML = fresh.innerHTML;
          }
          if (data['cart-icon-bubble']) {
            var doc2 = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
            var freshCount = doc2.querySelector('[data-cart-count]');
            qsa('[data-cart-count]').forEach(function (el) {
              if (!freshCount) return;
              el.textContent = freshCount.textContent;
              el.setAttribute('data-count', freshCount.getAttribute('data-count') || '0');
            });
          }
          if (openAfter) self.open();
        })
        .catch(function () { self.fallbackRefresh(openAfter); });
    },

    /* If section rendering is unavailable, at least keep the count honest */
    fallbackRefresh: function (openAfter) {
      var self = this;
      return fetch(ROUTES.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          qsa('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
            el.setAttribute('data-count', String(cart.item_count));
          });
          if (openAfter) self.open();
        })
        .catch(function () {});
    },

    add: function (formData, onError) {
      var self = this;
      if (this.busy) return Promise.resolve();
      this.setBusy(true);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      return fetch(ROUTES.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) { return r.json().then(function (b) { return { ok: r.ok, body: b }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body.description || res.body.message || STRINGS.cartError);
          announce(STRINGS.added);
          return self.refresh(T.cartType === 'drawer');
        })
        .catch(function (err) {
          if (onError) onError(err.message || STRINGS.cartError);
          else announce(err.message || STRINGS.cartError);
        })
        .then(function () { self.setBusy(false); });
    },

    change: function (line, quantity) {
      var self = this;
      if (this.busy) return Promise.resolve();
      this.setBusy(true);
      return fetch(ROUTES.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity, sections: 'cart-drawer,cart-icon-bubble,main-cart' })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart.sections && cart.sections['main-cart']) {
            var doc = new DOMParser().parseFromString(cart.sections['main-cart'], 'text/html');
            var fresh = doc.querySelector('[data-cart-page-inner]');
            var target = qs('[data-cart-page-inner]');
            if (fresh && target) target.innerHTML = fresh.innerHTML;
          }
          return self.refresh(false);
        })
        .catch(function () { announce(STRINGS.cartError); })
        .then(function () { self.setBusy(false); });
    },

    updateNote: debounce(function (note) {
      fetch(ROUTES.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      }).catch(function () {});
    }, 600)
  };

  window.LighthouseCart = Cart;

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-cart-open]');
    if (openBtn && T.cartType === 'drawer') {
      e.preventDefault();
      Cart.open();
      return;
    }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); Cart.close(); return; }

    var step = e.target.closest('[data-qty-step]');
    if (step) {
      e.preventDefault();
      var wrap = step.closest('[data-qty]');
      var field = wrap && qs('input', wrap);
      if (!field) return;
      var min = parseInt(field.min, 10) || 1;
      var max = field.max ? parseInt(field.max, 10) : null;
      var next = (parseInt(field.value, 10) || min) + parseInt(step.dataset.qtyStep, 10);
      if (next < min) next = min;
      if (max !== null && next > max) { next = max; announce(STRINGS.quantityError); }
      if (String(next) === field.value) return;
      field.value = next;
      field.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    var removeBtn = e.target.closest('[data-line-remove]');
    if (removeBtn) {
      e.preventDefault();
      Cart.change(removeBtn.dataset.lineRemove, 0);
      return;
    }
  });

  document.addEventListener('change', function (e) {
    var lineField = e.target.closest('[data-line-quantity]');
    if (lineField) {
      Cart.change(lineField.dataset.lineQuantity, parseInt(lineField.value, 10) || 0);
      return;
    }
    var note = e.target.closest('[data-cart-note]');
    if (note) Cart.updateNote(note.value);
  });

  document.addEventListener('input', function (e) {
    var note = e.target.closest('[data-cart-note]');
    if (note) Cart.updateNote(note.value);
  });

  /* --- product form ------------------------------------------------------ */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();

    var button = qs('[data-add-button]', form);
    var label = qs('[data-add-label]', form);
    var errorEl = qs('[data-form-error]', form);
    var original = label ? label.textContent : '';

    if (errorEl) errorEl.textContent = '';
    if (button) button.setAttribute('aria-disabled', 'true');
    if (label) label.textContent = STRINGS.adding;

    Cart.add(new FormData(form), function (message) {
      if (errorEl) errorEl.textContent = message;
      else announce(message);
    }).then(function () {
      if (button) button.removeAttribute('aria-disabled');
      if (label) label.textContent = original || STRINGS.addToCart;
    });
  });

  /* --- variant picker ---------------------------------------------------- */
  qsa('[data-product-root]').forEach(function (root) {
    var dataEl = qs('[data-variant-data]', root);
    if (!dataEl) return;

    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
    if (!variants || !variants.length) return;

    var idInput = qs('[data-variant-id]', root);
    var priceEl = qs('[data-variant-price]', root);
    var skuEl = qs('[data-variant-sku]', root);
    var skuRow = qs('[data-variant-sku-row]', root) || skuEl;
    var inventoryEl = qs('[data-variant-inventory]', root);
    var button = qs('[data-add-button]', root);
    var label = qs('[data-add-label]', root);
    var groups = qsa('[data-option-index]', root);
    var updateUrl = root.dataset.updateUrl !== 'false';

    function selectedOptions() {
      return groups.map(function (group) {
        var checked = qs('input:checked', group);
        if (checked) return checked.value;
        var select = qs('select', group);
        return select ? select.value : null;
      });
    }

    function findVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var match = true;
        for (var j = 0; j < options.length; j++) {
          if (options[j] != null && v.options[j] !== options[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    /* Cross out option values that produce no purchasable variant */
    function markAvailability(options) {
      groups.forEach(function (group, index) {
        qsa('input', group).forEach(function (input) {
          var probe = options.slice();
          probe[index] = input.value;
          // only constrain by earlier groups, so users can always move forward
          for (var k = index + 1; k < probe.length; k++) probe[k] = null;
          var candidates = variants.filter(function (v) {
            for (var j = 0; j <= index; j++) {
              if (probe[j] != null && v.options[j] !== probe[j]) return false;
            }
            return true;
          });
          var available = candidates.some(function (v) { return v.available; });
          var wrapper = input.closest('label') || input.parentNode;
          if (wrapper) wrapper.classList.toggle('is-unavailable', !available);
        });
      });
    }

    function sync() {
      var options = selectedOptions();
      var variant = findVariant(options) || null;
      markAvailability(options);

      groups.forEach(function (group) {
        var legendValue = qs('[data-option-value]', group);
        var checked = qs('input:checked', group);
        if (legendValue && checked) legendValue.textContent = checked.value;
      });

      if (!variant) {
        if (button) button.setAttribute('aria-disabled', 'true');
        if (button) button.disabled = true;
        if (label) label.textContent = STRINGS.unavailable;
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (priceEl && variant.price != null) {
        var onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
        priceEl.innerHTML =
          '<span class="price ' + (onSale ? 'price--sale' : '') + '">' +
            '<span class="price__now">' + formatMoney(variant.price) + '</span>' +
            (onSale ? '<span class="price__was">' + formatMoney(variant.compare_at_price) + '</span>' : '') +
          '</span>';
      }

      if (skuEl) {
        skuEl.textContent = variant.sku || '';
        skuRow.hidden = !variant.sku;
      }

      if (inventoryEl) {
        inventoryEl.hidden = false;
        inventoryEl.classList.remove('stock--low', 'stock--out');
        if (!variant.available) {
          inventoryEl.classList.add('stock--out');
          inventoryEl.textContent = inventoryEl.dataset.outText || STRINGS.soldOut;
        } else if (variant.inventory_management && variant.inventory_quantity != null && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
          inventoryEl.classList.add('stock--low');
          inventoryEl.textContent = (inventoryEl.dataset.lowText || '').replace('[qty]', variant.inventory_quantity);
        } else {
          inventoryEl.textContent = inventoryEl.dataset.inText || '';
        }
      }

      if (button) {
        button.disabled = !variant.available;
        if (variant.available) button.removeAttribute('aria-disabled');
        else button.setAttribute('aria-disabled', 'true');
      }
      if (label) label.textContent = variant.available ? STRINGS.addToCart : STRINGS.soldOut;

      /* scroll the matching gallery media into view */
      if (variant.featured_media && variant.featured_media.id) {
        var media = qs('[data-media-id="' + variant.featured_media.id + '"]', root);
        if (media && media.scrollIntoView) media.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      if (updateUrl && window.history && history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      }
    }

    groups.forEach(function (group) {
      on(group, 'change', sync);
    });
    sync();
  });

  /* --- quick add (product cards) ----------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-quick-add]');
    if (!form) return;
    e.preventDefault();
    var label = qs('[data-add-label]', form);
    var original = label ? label.textContent : '';
    if (label) label.textContent = STRINGS.adding;
    Cart.add(new FormData(form)).then(function () {
      if (label) label.textContent = original;
    });
  });

  /* --- collection facets (ajax, with real form fallback) ----------------- */
  (function facets() {
    var form = qs('[data-facet-form]');
    if (!form) return;

    var grid = qs('[data-grid-wrapper]');
    if (!grid) return;
    var sectionId = form.dataset.sectionId;
    if (!sectionId) return;

    function load(url, push) {
      grid.classList.add('is-loading');
      fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          ['[data-grid-wrapper]', '[data-facet-wrapper]', '[data-toolbar-count]'].forEach(function (sel) {
            var fresh = doc.querySelector(sel);
            var target = qs(sel);
            if (fresh && target) target.innerHTML = fresh.innerHTML;
          });
          if (push) history.pushState({ url: url }, '', url);
          grid.classList.remove('is-loading');
          var head = qs('[data-toolbar]');
          if (head && head.scrollIntoView) head.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(function () { window.location.href = url; });
    }

    function urlFromForm() {
      var params = new URLSearchParams(new FormData(form));
      // strip empties so the URL stays readable
      var clean = new URLSearchParams();
      params.forEach(function (value, key) { if (value !== '') clean.append(key, value); });
      var qsStr = clean.toString();
      return window.location.pathname + (qsStr ? '?' + qsStr : '');
    }

    document.addEventListener('change', function (e) {
      if (!e.target.closest('[data-facet-form]')) return;
      load(urlFromForm(), true);
    });

    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-facet-link]');
      if (!link) return;
      e.preventDefault();
      load(link.getAttribute('href'), true);
    });

    on(window, 'popstate', function () { load(window.location.href, false); });
  })();

  /* --- sort select (works with or without facets) ------------------------ */
  qsa('[data-sort-form]').forEach(function (form) {
    on(form, 'change', function () {
      if (form.closest('[data-facet-form]')) return;
      form.submit();
    });
  });

  /* --- localization forms ------------------------------------------------ */
  qsa('[data-locale-form] select').forEach(function (select) {
    on(select, 'change', function () { select.form.submit(); });
  });

  /* --- share button ------------------------------------------------------ */
  qsa('[data-share]').forEach(function (btn) {
    on(btn, 'click', function () {
      var url = btn.dataset.share || window.location.href;
      if (navigator.share) {
        navigator.share({ url: url, title: document.title }).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(url).then(function () {
          announce(btn.dataset.copiedLabel || 'Link copied');
        });
      }
    });
  });

  /* --- address form (customer addresses) --------------------------------- */
  qsa('[data-address-form]').forEach(function (wrapper) {
    var countrySelect = qs('[data-address-country]', wrapper);
    var provinceWrap = qs('[data-address-province-wrapper]', wrapper);
    var provinceSelect = qs('[data-address-province]', wrapper);
    if (!countrySelect || !provinceSelect || !provinceWrap) return;

    function syncProvinces() {
      var option = countrySelect.options[countrySelect.selectedIndex];
      var raw = option ? option.getAttribute('data-provinces') : null;
      var provinces = [];
      try { provinces = JSON.parse(raw || '[]'); } catch (err) { provinces = []; }
      if (!provinces.length) {
        provinceWrap.hidden = true;
        provinceSelect.innerHTML = '';
        return;
      }
      var current = provinceSelect.dataset.selected || '';
      provinceSelect.innerHTML = provinces.map(function (p) {
        return '<option value="' + p[0] + '"' + (p[0] === current ? ' selected' : '') + '>' + p[1] + '</option>';
      }).join('');
      provinceWrap.hidden = false;
    }

    on(countrySelect, 'change', syncProvinces);
    syncProvinces();
  });

  qsa('[data-address-toggle]').forEach(function (btn) {
    on(btn, 'click', function () {
      var target = qs(btn.dataset.addressToggle);
      if (!target) return;
      var isHidden = target.hidden;
      target.hidden = !isHidden;
      btn.setAttribute('aria-expanded', String(isHidden));
    });
  });

  qsa('[data-address-delete]').forEach(function (form) {
    on(form, 'submit', function (e) {
      if (!window.confirm(form.dataset.addressDelete)) e.preventDefault();
    });
  });

  /* --- reveal on scroll -------------------------------------------------- */
  (function reveal() {
    if (!document.body.classList.contains('animations-on')) return;
    var targets = qsa('[data-reveal]');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    targets.forEach(function (el) { io.observe(el); });
  })();

  /* --- Shopify theme editor hooks ---------------------------------------- */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-cart-drawer]')) return;
    qsa('[data-reveal]', e.target).forEach(function (el) { el.classList.add('is-revealed'); });
  });
  document.addEventListener('shopify:block:select', function (e) {
    if (e.target.scrollIntoView) e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();
